import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar } from 'react-icons/fi';
import SimpleCalendar from '../../atoms/SimpleCalendar';

interface DateRangeFilterProps {
    dateFrom: string;
    dateTo: string;
    onChange: (field: 'dateFrom' | 'dateTo', value: string) => void;
}

const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
};

const dateToISO = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const parseISO = (str: string): Date | null => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export default function DateRangeFilter({ dateFrom, dateTo, onChange }: DateRangeFilterProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selecting, setSelecting] = useState<'from' | 'to'>('from');
    const containerRef = useRef<HTMLDivElement>(null);

    const isInvalid = dateFrom && dateTo && dateFrom > dateTo;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDateSelect = (date: Date) => {
        const iso = dateToISO(date);
        if (selecting === 'from') {
            onChange('dateFrom', iso);
            setSelecting('to');
        } else {
            onChange('dateTo', iso);
            setIsOpen(false);
            setSelecting('from');
        }
    };

    const handleClear = () => {
        onChange('dateFrom', '');
        onChange('dateTo', '');
        setIsOpen(false);
        setSelecting('from');
    };

    const hasValue = dateFrom || dateTo;
    const displayText = hasValue
        ? `${formatDisplayDate(dateFrom) || '...'} - ${formatDisplayDate(dateTo) || '...'}`
        : 'Selecionar período';

    return (
        <div className="relative" ref={containerRef}>
            {/* Label */}
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block font-medium">
                Último Monitoramento
            </label>

            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    inline-flex items-center gap-2 px-3 py-2 text-sm border rounded
                    bg-white dark:bg-slate-800 
                    ${isInvalid
                        ? 'border-red-400 text-red-600 dark:border-red-500 dark:text-red-400'
                        : hasValue
                            ? 'border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300'
                            : 'border-gray-300 text-gray-600 dark:border-slate-600 dark:text-gray-400'
                    }
                    hover:border-blue-500 dark:hover:border-blue-400 transition-colors
                `}
                aria-label="Filtrar por período do último monitoramento"
            >
                <FiCalendar size={14} />
                <span className="whitespace-nowrap">{displayText}</span>
            </button>

            {/* Popover */}
            {isOpen && (
                <div className="absolute z-20 mt-2 left-0 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-xl p-4 w-[300px]">
                    {/* Tab selector */}
                    <div className="flex gap-2 mb-3">
                        <button
                            type="button"
                            onClick={() => setSelecting('from')}
                            className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-colors ${
                                selecting === 'from'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                            }`}
                        >
                            De: {formatDisplayDate(dateFrom) || '—'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelecting('to')}
                            className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-colors ${
                                selecting === 'to'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                            }`}
                        >
                            Até: {formatDisplayDate(dateTo) || '—'}
                        </button>
                    </div>

                    {/* Calendar */}
                    <SimpleCalendar
                        selectedDate={selecting === 'from' ? parseISO(dateFrom) : parseISO(dateTo)}
                        onDateChange={handleDateSelect}
                    />

                    {/* Validation error */}
                    {isInvalid && (
                        <p className="text-xs text-red-500 mt-2" role="alert">
                            Data De não pode ser posterior a Data Até.
                        </p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-3 border-t border-gray-200 dark:border-slate-600 pt-3">
                        <button
                            type="button"
                            onClick={handleClear}
                            className="flex-1 text-xs py-1.5 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                        >
                            Limpar
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="flex-1 text-xs py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
